// file: src/pages/DoNotTow.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  createDoNotTow,
  listDoNotTow,
  updateDoNotTow,
  deleteDoNotTow,
} from "../api/adminApi";
import Loader from "../components/Loader";

const BLUE = "#2A7EFA";
const BLUE_LIGHT = "#5AA9FF";

function clsx(...p) {
  return p.filter(Boolean).join(" ");
}
function normalizePlate(s) {
  return s.toUpperCase().replace(/\s+/g, "").slice(0, 24);
}
function fmtParts(dt) {
  try {
    const d = new Date(dt);
    return {
      date: d.toLocaleDateString(),
      time: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    };
  } catch {
    return { date: dt, time: "" };
  }
}
const pad = (n) => String(n).padStart(2, "0");
function toLocalISODate(d = new Date()) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function toLocalISOTime(d = new Date()) {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function mapDoNotTowApiRow(row) {
  return {
    id:
      row.id ??
      `dnt_${row.license_plate || ""}_${row.start_date || ""}_${
        row.start_time || ""
      }`,
    facilityName: row.facility_name || "",
    licensePlate: row.license_plate || "",
    startDate: row.start_date || "",
    startTime: (row.start_time || "").slice(0, 5),
    endDate: row.end_date || "",
    endTime: (row.end_time || "").slice(0, 5),
    relocated: !!row.is_relocated,
    relocatedFacilityName: row.relocated_facility_name || "",
    source: row.source || "manual",
    isPermanent: !!row.is_permanent,
    permanent: !!row.is_permanent,
  };
}

/* Badges & Pills */
function PermanentBadge() {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-1 text-xs font-bold text-indigo-900 ring-1 ring-indigo-100">
      Permanent
    </span>
  );
}
function DntBadge() {
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-rose-50 px-3 py-1 text-xs font-medium text-rose-700 ring-1 ring-rose-100">
      <span className="h-2 w-2 rounded-full bg-rose-500" /> Do Not Tow
    </span>
  );
}
function RelocatedBadge({ value }) {
  return value ? (
    <span className="inline-flex items-center gap-2 rounded-full bg-violet-50 px-3 py-1 text-xs font-medium text-violet-700 ring-1 ring-violet-100">
      <span className="h-2 w-2 rounded-full bg-violet-500" /> Relocated facility
    </span>
  ) : (
    <span className="inline-flex items-center gap-2 rounded-full bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
      <span className="h-2 w-2 rounded-full bg-slate-400" /> Not relocated
    </span>
  );
}
function PlatePill({ plate }) {
  if (!plate) return null;
  return (
    <span
      className="inline-flex max-w-full items-center gap-2 rounded-full px-3 py-1 text-white shadow whitespace-nowrap leading-none"
      style={{ background: BLUE, boxShadow: `0 8px 20px ${BLUE}22` }}
      title={plate}
    >
      <svg
        viewBox="0 0 24 24"
        className="h-4 w-4 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        aria-hidden
      >
        <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
        <path
          d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
          fill="currentColor"
        />
      </svg>
      <span
        className="font-mono tracking-wider"
        style={{ fontSize: "clamp(12px,1.6vw,14px)" }}
      >
        {plate}
      </span>
    </span>
  );
}

/* Animated Modal */
function Modal({ open, onClose, title, children }) {
  const overlayRef = useRef(null);
  if (!open) return null;
  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 grid place-items-center p-4"
      onMouseDown={(e) => e.target === overlayRef.current && onClose()}
    >
      <div className="absolute inset-0 transition-opacity duration-200" />
      <div className="relative z-10 w-full max-w-2xl rounded-2xl border border-black/[0.06] bg-white p-5 shadow-2xl transition-all duration-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
          <button
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-black/[0.08] hover:bg-black/[0.03] active:scale-95 transition"
          >
            <svg
              viewBox="0 0 24 24"
              className="h-5 w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
}

/* Confirm dialog */
function Confirm({ open, onCancel, onConfirm, title, body }) {
  if (!open) return null;
  return (
    <Modal open={open} onClose={onCancel} title={title}>
      <p className="text-sm text-slate-600">{body}</p>
      <div className="mt-5 flex justify-end gap-2">
        <button
          onClick={onCancel}
          className="rounded-xl border border-black/[0.06] px-4 py-2 text-sm hover:bg-black/[0.03] transition"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white shadow hover:shadow-lg active:scale-[0.99] transition"
          style={{ boxShadow: "0 8px 20px rgba(225,29,72,.25)" }}
        >
          Delete
        </button>
      </div>
    </Modal>
  );
}

/* Card view */
function DntCard({ r }) {
  const s = fmtParts(`${r.startDate}T${r.startTime}`);
  const e = r.isPermanent
    ? { date: "Permanent", time: "No expiration date" }
    : fmtParts(`${r.endDate}T${r.endTime}`);
  const plate = normalizePlate(r.licensePlate);

  return (
    <article className="rounded-2xl border border-black/[0.06] bg-white p-4 sm:p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="grid h-9 w-9 sm:h-10 sm:w-10 place-items-center rounded-xl text-white shadow shrink-0"
            style={{
              background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
              boxShadow: `0 8px 16px ${BLUE}20`,
            }}
          >
            <svg
              viewBox="0 0 24 24"
              className="h-4.5 w-4.5 sm:h-5 sm:w-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M4 13l1.4-4.2A2 2 0 0 1 7.3 7h9.4a2 2 0 0 1 1.9 1.8L20 13" />
              <path
                d="M3 13h18v4a1 1 0 0 1-1 1h-1.5a1 1 0 0 1-1-1v-1H6.5v1a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-4z"
                fill="currentColor"
              />
            </svg>
          </div>

          <div className="min-w-0">
            <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
              Facility
            </div>
            <div className="truncate text-sm sm:text-base font-semibold leading-6 tracking-tight text-slate-800">
              {r.facilityName}
            </div>

            {r.relocated && r.relocatedFacilityName && (
              <div className="text-[11px] sm:text-xs text-violet-700/80 truncate">
                → to: {r.relocatedFacilityName}
              </div>
            )}
          </div>
        </div>

        {/* Badges wrap cleanly on mobile */}
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          <DntBadge />
          {r.isPermanent ? <PermanentBadge /> : null}
          <RelocatedBadge value={r.relocated} />
        </div>
      </div>

      {/* Times + Plate */}
      <div
        className="
          mt-4 grid grid-cols-1 gap-3
          sm:grid-cols-2
          xl:grid-cols-[1fr,1fr,240px]
        "
      >
        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0">
          <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
            Start time
          </div>
          <div className="mt-1 text-sm font-medium leading-5 text-slate-800">
            {s.date}
          </div>
          <div className="text-sm leading-5 text-slate-700">{s.time}</div>
        </div>

        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0">
          <div className="text-[11px] sm:text-xs leading-4 text-slate-500">
            End time
          </div>
          <div className="mt-1 text-sm font-medium leading-5 text-slate-800">
            {e.date}
          </div>
          <div className="text-sm leading-5 text-slate-700">{e.time}</div>
        </div>

        <div className="rounded-xl border border-black/[0.06] p-3 min-w-0 overflow-hidden sm:col-span-2 xl:col-span-1">
          <div className="mb-2 text-[11px] sm:text-xs leading-4 text-slate-500">
            License Plate
          </div>
          <div className="flex w-full justify-start min-w-0">
            <div className="min-w-0">
              <PlatePill plate={plate} />
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

/* Table row */
function TableRow({ r, onEdit, onDelete }) {
  const s = fmtParts(`${r.startDate}T${r.startTime}`);
  const e = r.isPermanent
    ? { date: "Permanent", time: "No expiration date" }
    : fmtParts(`${r.endDate}T${r.endTime}`);
  return (
    <tr className="border-b last:border-0">
      <td className="px-4 py-3">
        <div className="font-medium">{r.facilityName}</div>
        {r.relocated && r.relocatedFacilityName && (
          <div className="text-xs text-violet-700/80">
            → to: {r.relocatedFacilityName}
          </div>
        )}
      </td>
      <td className="px-4 py-3">
        <PlatePill plate={normalizePlate(r.licensePlate)} />
      </td>
      <td className="px-4 py-3">
        <div className="text-sm font-medium">{s.date}</div>
        <div className="text-xs text-slate-600">{s.time}</div>
      </td>
      <td className="px-4 py-3">
        <div className="text-sm font-medium">{e.date}</div>
        <div className="text-xs text-slate-600">{e.time}</div>
      </td>
      <td className="px-4 py-3">
        <DntBadge />
      </td>
      <td className="px-4 py-3">
        <RelocatedBadge value={r.relocated} />
      </td>
      <td className="px-4 py-3">
        <div className="flex gap-2">
          <button
            onClick={() => onEdit(r)}
            className="rounded-lg border border-black/[0.06] px-3 py-1.5 text-sm hover:bg-black/[0.03] transition"
          >
            Edit
          </button>
          <button
            onClick={() => onDelete(r)}
            className="rounded-lg bg-rose-600 px-3 py-1.5 text-sm text-white hover:shadow active:scale-[0.99] transition"
          >
            Delete
          </button>
        </div>
      </td>
    </tr>
  );
}

/* Toggle */
function Switch({ checked, onChange, label }) {
  return (
    <label className="flex items-center gap-3 select-none">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={clsx(
          "relative inline-flex h-6 w-11 items-center rounded-full transition",
          checked ? "bg-violet-600" : "bg-slate-300"
        )}
      >
        <span
          className={clsx(
            "inline-block h-5 w-5 transform rounded-full bg-white transition",
            checked ? "translate-x-5" : "translate-x-1"
          )}
        />
      </button>
      <span className="text-sm font-medium text-slate-800">{label}</span>
    </label>
  );
}

/* Main page */
export default function DoNotTow() {
  const [items, setItems] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState("");
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [view, setView] = useState("cards"); // "cards" | "table"
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  // pagination for table
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        setListError("");
        setListLoading(true);

        const res = await listDoNotTow(
          {
            skip: 0,
            limit: 10000,
            active_only: false,
            search: debounced || undefined, // search by plate or facility name
          },
          { signal: ac.signal }
        );

        const rows = Array.isArray(res)
          ? res
          : res?.results || res?.items || [];
        setItems(rows.map(mapDoNotTowApiRow));
      } catch (e) {
        if (!ac.signal.aborted) {
          console.error("Failed to load Do Not Tow list:", e);
          setListError(
            e?.payload?.message ||
              e?.message ||
              "Failed to load Do Not Tow list."
          );
        }
      } finally {
        if (!ac.signal.aborted) setListLoading(false);
      }
    })();

    return () => ac.abort();
  }, [debounced]);

  // search debounce
  useEffect(() => {
    const t = setTimeout(() => setDebounced(normalizePlate(query)), 250);
    return () => clearTimeout(t);
  }, [query]);
  useEffect(() => {
    setPage(1);
  }, [debounced, view]);

  const dataset = useMemo(() => {
    if (!debounced) return items;
    return items.filter((r) =>
      normalizePlate(r.licensePlate).includes(debounced)
    );
  }, [debounced, items]);

  const total = dataset.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const startIdx = (page - 1) * pageSize;
  const pageItems = dataset.slice(startIdx, startIdx + pageSize);

  const now = new Date();
  const defaultValues = {
    facilityName: "",
    licensePlate: "",
    startDate: toLocalISODate(now),
    startTime: toLocalISOTime(now),
    endDate: toLocalISODate(now),
    endTime: toLocalISOTime(now),
    relocated: false,
    relocatedFacilityName: "",
    permanent: false,
  };

  const openAdd = () => {
    setEditingId(null);
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditingId(r.id);
    setOpen(true);
  };

  const askDelete = (r) => {
    setToDelete(r);
    setConfirmOpen(true);
  };
  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      // prefer numeric/server id when available
      const idForApi =
        typeof toDelete.id === "number"
          ? toDelete.id
          : Number.isFinite(Number(toDelete.id))
          ? Number(toDelete.id)
          : toDelete.id; // fallback (in case server used string IDs)

      await deleteDoNotTow(idForApi);
      setItems((prev) => prev.filter((x) => x.id !== toDelete.id));
    } catch (e) {
      alert(
        e?.payload?.message || e?.message || "Failed to delete Do Not Tow."
      );
    } finally {
      setConfirmOpen(false);
      setToDelete(null);
    }
  };

  const Schema = Yup.object({
    facilityName: Yup.string().trim().required("Facility name is required"),
    licensePlate: Yup.string().trim().required("License plate is required"),
    startDate: Yup.string().required("Start date is required"),
    startTime: Yup.string().required("Start time is required"),
    endDate: Yup.string().when("permanent", {
      is: true,
      then: (s) => s.optional(),
      otherwise: (s) => s.required("End date is required"),
    }),
    endTime: Yup.string().when("permanent", {
      is: true,
      then: (s) => s.optional(),
      otherwise: (s) => s.required("End time is required"),
    }),
    permanent: Yup.boolean(),
    relocated: Yup.boolean(),
    relocatedFacilityName: Yup.string()
      .when("relocated", {
        is: true,
        then: (s) => s.trim().required("Relocated facility name is required"),
        otherwise: (s) => s.trim().optional(),
      })
      .max(80, "Too long"),
  });

  const getInitialValues = () => {
    if (!editingId) return defaultValues;
    const r = items.find((x) => x.id === editingId);
    if (!r) return defaultValues;
    return {
      facilityName: r.facilityName || "",
      licensePlate: r.licensePlate || "",
      startDate: r.startDate || toLocalISODate(now),
      startTime: r.startTime || toLocalISOTime(now),
      endDate: r.endDate || toLocalISODate(now),
      endTime: r.endTime || toLocalISOTime(now),
      relocated: !!r.relocated,
      relocatedFacilityName: r.relocatedFacilityName || "",
      permanent: !!r.isPermanent,
    };
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    const toHHMMSS = (t) =>
      t && /^\d{2}:\d{2}(:\d{2})?$/.test(t)
        ? t.length === 5
          ? `${t}:00`
          : t
        : "00:00:00";

    // map form → API
    const editingRow = editingId
      ? items.find((x) => x.id === editingId)
      : null;

    const payload = {
      facility_name: values.facilityName.trim(),
      license_plate: normalizePlate(values.licensePlate),
      start_date: values.startDate,
      start_time: toHHMMSS(values.startTime),
      is_relocated: !!values.relocated,
      relocated_facility_name: values.relocated
        ? values.relocatedFacilityName.trim()
        : undefined,
      requester_name: "",
      requester_contact: "",
      reason: editingId ? "Updated from portal" : "Created from portal",
      source: editingRow?.source || "manual",
      is_permanent: !!values.permanent,
    };

    if (!values.permanent) {
      payload.end_date = values.endDate;
      payload.end_time = toHHMMSS(values.endTime);
    }

    try {
      if (editingId) {
        // PUT update
        const serverRow = await updateDoNotTow(editingId, payload);
        // Prefer server’s version if returned; otherwise patch locally
        const updated =
          serverRow && (serverRow.id || serverRow.license_plate)
            ? mapDoNotTowApiRow(serverRow)
            : {
                id: editingId,
                facilityName: values.facilityName.trim(),
                licensePlate: normalizePlate(values.licensePlate),
                startDate: values.startDate,
                startTime: values.startTime,
                endDate: values.endDate,
                endTime: values.endTime,
                relocated: !!values.relocated,
                relocatedFacilityName: values.relocated
                  ? values.relocatedFacilityName.trim()
                  : "",
              };

        setItems((prev) => prev.map((x) => (x.id === editingId ? updated : x)));
      } else {
        // POST create
        const serverRow = await createDoNotTow(payload);
        const created =
          serverRow && (serverRow.id || serverRow.license_plate)
            ? mapDoNotTowApiRow(serverRow)
            : {
                id: `dnt_${Date.now()}`,
                facilityName: values.facilityName.trim(),
                licensePlate: normalizePlate(values.licensePlate),
                startDate: values.startDate,
                startTime: values.startTime,
                endDate: values.endDate,
                endTime: values.endTime,
                relocated: !!values.relocated,
                relocatedFacilityName: values.relocated
                  ? values.relocatedFacilityName.trim()
                  : "",
              };

        setItems((prev) => [created, ...prev]);
      }

      setOpen(false);
    } catch (e) {
      alert(e?.payload?.message || e?.message || "Failed to save Do Not Tow.");
    } finally {
      setSubmitting(false);
    }
  };
  // time order check (Formik-level helper)
  const timeOrderError = (values) => {
    if (values.permanent) return "";
    const s = new Date(`${values.startDate}T${values.startTime}`);
    const e = new Date(`${values.endDate}T${values.endTime}`);
    return isFinite(s) && isFinite(e) && e < s
      ? "End time must be after Start time."
      : "";
  };

  return (
    <div className="space-y-6 antialiased text-slate-800">
      {/* Header: search + CTA */}
      <section className="rounded-3xl border border-black/[0.06] bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div className="flex-1">
            <label
              htmlFor="dnt-search"
              className="block text-sm font-medium leading-5 text-slate-800"
            >
              Do Not Tow — search by license plate
            </label>
            <div className="relative mt-1">
              <input
                id="dnt-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. ABC1234 or IL-7XZ890"
                className="w-full rounded-xl border border-black/[0.06] bg-white px-4 py-3 text-[15px] leading-5 outline-none transition focus:ring-4"
                style={{ borderColor: "rgba(0,0,0,.06)" }}
              />
              <div className="pointer-events-none absolute inset-y-0 right-3 grid place-items-center text-slate-400">
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.5-3.5" />
                </svg>
              </div>
            </div>
            {debounced ? (
              <div className="mt-3 text-xs text-slate-600">
                Searching for: <PlatePill plate={debounced} />
              </div>
            ) : (
              <p className="mt-3 text-xs leading-4 text-slate-500">
                Add details of <strong>Do Not Tow</strong> for reservation
                details, then search to verify.
              </p>
            )}
          </div>
          <div className="flex items-end gap-2">
            <div className="rounded-xl border border-black/[0.06] p-1 bg-white">
              <button
                className={clsx(
                  "rounded-lg px-3 py-2 text-sm",
                  view === "cards"
                    ? "bg-black/[0.04] font-semibold"
                    : "text-slate-700"
                )}
                onClick={() => setView("cards")}
              >
                Cards
              </button>
              <button
                className={clsx(
                  "rounded-lg px-3 py-2 text-sm",
                  view === "table"
                    ? "bg-black/[0.04] font-semibold"
                    : "text-slate-700"
                )}
                onClick={() => setView("table")}
              >
                Table
              </button>
            </div>
            <button
              onClick={openAdd}
              className="rounded-xl bg-[color:var(--blue)] px-4 py-3 text-sm font-semibold text-white shadow hover:shadow-lg active:scale-[0.99] transition"
              style={{ ["--blue"]: BLUE, boxShadow: `0 10px 24px ${BLUE}26` }}
            >
              + Add Do Not Tow
            </button>
          </div>
        </div>
      </section>

      {/* Summary */}
      <div className="flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <h3 className="text-base font-semibold tracking-tight">
          Do Not Tow list
        </h3>
        <div className="text-sm leading-5 text-slate-600">
          Showing <span className="font-medium text-slate-800">{total}</span>{" "}
          entr{total === 1 ? "y" : "ies"}
        </div>
      </div>

      {listLoading ? (
        <div className="grid place-items-center rounded-3xl border border-black/[0.06] bg-white p-12 min-h-[220px]">
          <Loader
            size="lg"
            label={
              debounced ? `Searching ${debounced}…` : "Loading Do Not Tow…"
            }
          />
        </div>
      ) : listError ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          {listError}
        </div>
      ) : total === 0 ? (
        <div className="grid place-items-center rounded-3xl border border-black/[0.06] bg-white p-12 text-center text-slate-600">
          {debounced ? (
            <>
              No Do Not Tow entry for{" "}
              <strong className="mx-1">{debounced}</strong>.
            </>
          ) : (
            "No entries yet."
          )}
        </div>
      ) : view === "cards" ? (
        <div className="grid gap-5 md:grid-cols-2">
          {dataset.map((r) => (
            <DntCard key={r.id} r={r} />
          ))}
        </div>
      ) : (
        <div className="rounded-3xl border border-black/[0.06] bg-white p-4 shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-[1040px] w-full text-left">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-4 py-2">Facility</th>
                  <th className="px-4 py-2">License Plate</th>
                  <th className="px-4 py-2">Start</th>
                  <th className="px-4 py-2">End</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Relocated</th>
                  <th className="px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                {pageItems.map((r) => (
                  <TableRow
                    key={r.id}
                    r={r}
                    onEdit={openEdit}
                    onDelete={askDelete}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Showing{" "}
              <span className="font-medium">
                {Math.min(total, startIdx + 1)}
              </span>
              –
              <span className="font-medium">
                {Math.min(total, startIdx + pageSize)}
              </span>{" "}
              of <span className="font-medium">{total}</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                className="rounded-lg border border-black/[0.06] px-3 py-2 text-sm disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                Prev
              </button>
              <button
                className="rounded-lg border border-black/[0.06] px-3 py-2 text-sm disabled:opacity-50"
                onClick={() => setPage((p) => Math.min(p + 1, pageCount))}
                disabled={page === pageCount}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal (Formik) */}
      <Formik
        enableReinitialize
        initialValues={getInitialValues()}
        validationSchema={Schema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => {
          const orderError = timeOrderError(values);
          return (
            <Modal
              open={open}
              onClose={() => setOpen(false)}
              title={
                editingId
                  ? "Edit Do Not Tow entry"
                  : "Add details of Do Not Tow (reservation details)"
              }
            >
              <Form className="space-y-5">
                <div className="grid gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-800">
                      Facility name
                    </label>
                    <Field
                      name="facilityName"
                      className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-3 py-2 outline-none focus:ring-4"
                      placeholder="e.g. Downtown Garage A"
                    />
                    <ErrorMessage
                      name="facilityName"
                      component="div"
                      className="mt-1 text-xs text-rose-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-800">
                      License plate
                    </label>
                    <Field
                      name="licensePlate"
                      className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-3 py-2 outline-none focus:ring-4 font-mono"
                      placeholder="e.g. ABC1234"
                    />
                    <ErrorMessage
                      name="licensePlate"
                      component="div"
                      className="mt-1 text-xs text-rose-600"
                    />
                  </div>
                </div>

                {/* Dates & Times */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-slate-800">
                        Start date
                      </label>
                      <Field
                        type="date"
                        name="startDate"
                        className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-3 py-2 outline-none focus:ring-4"
                      />
                      <ErrorMessage
                        name="startDate"
                        component="div"
                        className="mt-1 text-xs text-rose-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-800">
                        Start time
                      </label>
                      <Field
                        type="time"
                        step="60"
                        name="startTime"
                        className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-3 py-2 outline-none focus:ring-4"
                      />
                      <ErrorMessage
                        name="startTime"
                        component="div"
                        className="mt-1 text-xs text-rose-600"
                      />
                    </div>
                  </div>
                  {!values.permanent && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-slate-800">
                        End date
                      </label>
                      <Field
                        type="date"
                        name="endDate"
                        className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-3 py-2 outline-none focus:ring-4"
                      />
                      <ErrorMessage
                        name="endDate"
                        component="div"
                        className="mt-1 text-xs text-rose-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-800">
                        End time
                      </label>
                      <Field
                        type="time"
                        step="60"
                        name="endTime"
                        className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-3 py-2 outline-none focus:ring-4"
                      />
                      <ErrorMessage
                        name="endTime"
                        component="div"
                        className="mt-1 text-xs text-rose-600"
                      />
                    </div>
                  </div>
                  )}
                </div>

                <Switch
                  checked={values.permanent}
                  onChange={(v) => setFieldValue("permanent", v)}
                  label="Permanent — no expiration"
                />

                {/* Relocated */}
                <div className="flex flex-col gap-2">
                  <Switch
                    checked={values.relocated}
                    onChange={(v) => setFieldValue("relocated", v)}
                    label="Relocated facility"
                  />
                  {values.relocated && (
                    <div>
                      <label className="block text-sm font-medium text-slate-800">
                        Relocated facility name
                      </label>
                      <Field
                        name="relocatedFacilityName"
                        className="mt-1 w-full rounded-xl border border-black/[0.06] bg-white px-3 py-2 outline-none focus:ring-4"
                        placeholder="e.g. Overflow Lot B"
                      />
                      <ErrorMessage
                        name="relocatedFacilityName"
                        component="div"
                        className="mt-1 text-xs text-rose-600"
                      />
                    </div>
                  )}
                </div>

                {orderError && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    {orderError}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    className="rounded-xl border border-black/[0.06] px-4 py-2 text-sm hover:bg-black/[0.03] transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!!orderError || isSubmitting}
                    className="rounded-xl bg-[color:var(--blue)] px-4 py-2 text-sm font-semibold text-white shadow hover:shadow-lg active:scale-[0.99] transition disabled:opacity-60"
                    style={{
                      ["--blue"]: BLUE,
                      boxShadow: `0 8px 20px ${BLUE}22`,
                    }}
                  >
                    {editingId ? "Save changes" : "Save entry"}
                  </button>
                </div>
              </Form>
            </Modal>
          );
        }}
      </Formik>

      {/* Delete confirm */}
      <Confirm
        open={confirmOpen}
        onCancel={() => {
          setConfirmOpen(false);
          setToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Do Not Tow entry?"
        body="This will permanently remove the entry from the Do Not Tow list."
      />
    </div>
  );
}
